import React, { useState } from 'react';
import { jobVerticalAPI, jobApiError } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
const money = (v, cur) => `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;

const STATUS_STYLE = {
  UNPAID: ['Unpaid', '#b45309'], PART_PAID: ['Part paid', '#b45309'], PAID: ['Paid', '#15803d'],
  REFUNDED: ['Refunded', '#475569'], PART_REFUNDED: ['Part refunded', '#475569'], WAIVED: ['Waived', '#475569'],
};

/**
 * Fees & payments for one job seeker. The rules live on the server
 * (job_clients/fees.py); this panel just shows what is blocking collection and
 * why, in plain words, so staff are never guessing:
 *   1. only fees on the written schedule can be added
 *   2. show the worker the charge, then record that they agreed
 *   3. job-linked fees open only after a verified employer's written offer
 *      (and a cooling-off period)
 *   4. every payment is receipted
 *   5. job-linked fees are refunded in full if the worker is not placed
 * Founder rule, 19 Oct 2026: only the CFO records a payment. `ledgerOnly` is the
 * CFO's Payments desk view: record payments, nothing else (the CFO isn't job
 * staff, so can't add, show or agree charges).
 */
export default function JobFeesPanel({ client, charges, schedule, onChanged, ledgerOnly = false }) {
  const { isAdmin, canRecordPayments } = useAuth();
  const admin = typeof isAdmin === 'function' ? isAdmin() : !!isAdmin;
  const isRecorder = canRecordPayments();
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState('');
  const [inputs, setInputs] = useState({}); // per-charge draft: method / amount / reason / reference
  const draft = (id) => inputs[id] || {};
  const setDraft = (id, k, v) => setInputs((m) => ({ ...m, [id]: { ...m[id], [k]: v } }));

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); setErr(''); await onChanged(); } catch (e) { setErr(jobApiError(e)); } finally { setBusy(false); }
  };

  const applicable = schedule.filter((s) => s.is_active
    && (!s.country || String(s.country) === String(client.destination))
    && (s.job_type === 'ANY' || s.job_type === client.job_type));
  const usable = applicable.filter((s) => !charges.some((c) => String(c.schedule_item) === String(s.id)
    && c.status !== 'WAIVED' && c.status !== 'REFUNDED'));

  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title"><span>Fees &amp; payments (worker-paid)</span></div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8, lineHeight: 1.45 }}>
        Only fees on the written schedule. Show the worker each charge and record their agreement before taking money.
        Job-linked fees open only after a verified employer's written offer, then a 24-hour cooling-off, and are refunded in
        full if the worker is not placed. Visa and travel costs paid to third parties go under expenses below.
      </div>
      {err && <div className="error-message" style={{ marginBottom: 8 }}>{err}</div>}

      {charges.length === 0 && <div className="cv-subsection-empty">No fees charged to this worker.</div>}
      {charges.map((c) => {
        const d = draft(c.id);
        const [stLabel, stColor] = STATUS_STYLE[c.status] || [c.status, '#475569'];
        const closed = ['WAIVED', 'REFUNDED', 'PART_REFUNDED'].includes(c.status);
        return (
          <div key={c.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', marginBottom: 8, background: '#fff' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <strong style={{ flex: 1 }}>{c.label}</strong>
              <span style={{ fontSize: 11, color: '#64748b' }}>{c.category === 'PLACEMENT' ? 'Job-linked' : 'Optional service'}</span>
              <span className="status-pill" style={{ color: stColor }}>{stLabel}</span>
            </div>
            <div style={{ fontSize: 12, color: '#475569', margin: '4px 0' }}>{c.description}</div>
            <div style={{ fontSize: 13 }}>
              {money(c.amount, c.currency)} · paid {money(c.paid, c.currency)}
              {c.status !== 'PAID' && !closed && <> · <strong>balance {money(c.balance, c.currency)}</strong></>}
              <span style={{ color: '#94a3b8', fontSize: 11 }}> · {c.refund_rule_display}</span>
            </div>

            <div style={{ fontSize: 12, marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span style={{ color: c.disclosed_at ? '#15803d' : '#b45309' }}>
                {c.disclosed_at ? `✓ Shown ${fmtDate(c.disclosed_at)} (${c.disclosed_method})` : '○ Not yet shown to the worker'}
              </span>
              <span style={{ color: c.acknowledged_at ? '#15803d' : '#b45309' }}>
                {c.acknowledged_at ? `✓ Agreed ${fmtDate(c.acknowledged_at)} (${c.acknowledged_method})` : '○ Worker has not agreed'}
              </span>
            </div>

            {!ledgerOnly && !c.disclosed_at && !closed && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <input style={{ flex: 1 }} placeholder="How was it shown? (printed schedule, WhatsApp, email…)" value={d.method || ''}
                  onChange={(e) => setDraft(c.id, 'method', e.target.value)} />
                <button className="btn-primary" disabled={busy || !(d.method || '').trim()}
                  onClick={() => run(() => jobVerticalAPI.charges.disclose(c.id, d.method.trim()))}>Record: shown</button>
              </div>
            )}
            {!ledgerOnly && c.disclosed_at && !c.acknowledged_at && !closed && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <input style={{ flex: 1 }} placeholder="How did the worker agree? (signed form, written reply, in person…)" value={d.method || ''}
                  onChange={(e) => setDraft(c.id, 'method', e.target.value)} />
                <button className="btn-primary" disabled={busy || !(d.method || '').trim()}
                  onClick={() => run(() => jobVerticalAPI.charges.acknowledge(c.id, d.method.trim()))}>Record: agreed</button>
              </div>
            )}

            {!closed && c.status !== 'PAID' && (
              c.collection_blockers.length > 0 ? (
                <div style={{ marginTop: 8, background: '#fef3c7', color: '#92400e', borderRadius: 8, padding: '6px 10px', fontSize: 12 }}>
                  <strong>Cannot collect yet:</strong>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>{c.collection_blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
                </div>
              ) : !isRecorder ? (
                <div style={{ marginTop: 8, color: '#15803d', fontSize: 12 }}>
                  Clear to collect. Only the CFO or Chairman records the payment (from Record Payments), and it gets its receipt then.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr auto', gap: 6, marginTop: 8 }}>
                  <input type="number" min="0" placeholder={`Amount (${c.currency})`} value={d.amount || ''} onChange={(e) => setDraft(c.id, 'amount', e.target.value)} />
                  <input placeholder="Method (cash, EcoCash…)" value={d.paymethod || ''} onChange={(e) => setDraft(c.id, 'paymethod', e.target.value)} />
                  <input placeholder="Reference (optional)" value={d.reference || ''} onChange={(e) => setDraft(c.id, 'reference', e.target.value)} />
                  <button className="btn-primary" disabled={busy || !d.amount || !(d.paymethod || '').trim()}
                    onClick={() => run(() => jobVerticalAPI.charges.pay(c.id, { amount: d.amount, method: d.paymethod.trim(), reference: d.reference || '' }))}>
                    Record payment
                  </button>
                </div>
              )
            )}

            {c.payments.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12 }}>
                {c.payments.map((p) => (
                  <div key={p.id} style={{ color: '#334155' }}>
                    Receipt <strong>{p.receipt_number}</strong> — {money(p.amount, c.currency)} by {p.method}, {fmtDate(p.paid_at)}
                    {p.received_by_name ? ` (received by ${p.received_by_name})` : ''}
                  </div>
                ))}
              </div>
            )}
            {c.refunds.map((r) => (
              <div key={r.id} style={{ marginTop: 4, fontSize: 12, color: '#475569' }}>Refunded {money(r.amount, c.currency)} on {fmtDate(r.refunded_at)} — {r.reason}</div>
            ))}

            {Number(c.refund_due) > 0 && (
              <div style={{ marginTop: 8, background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '6px 10px', fontSize: 12 }}>
                <strong>Refund due: {money(c.refund_due, c.currency)}</strong> — this worker was not placed, so the job-linked fee is refundable in full.
                {admin ? (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <input style={{ flex: 1 }} placeholder="Reason / how refunded" value={d.reason || ''} onChange={(e) => setDraft(c.id, 'reason', e.target.value)} />
                    <button className="btn-primary" disabled={busy || (d.reason || '').trim().length < 5}
                      onClick={() => run(() => jobVerticalAPI.charges.refund(c.id, { amount: c.refund_due, reason: d.reason.trim() }))}>Record refund</button>
                  </div>
                ) : <div>An admin must record the refund.</div>}
              </div>
            )}

            {admin && !closed && c.status === 'UNPAID' && (
              <div style={{ marginTop: 6, textAlign: 'right' }}>
                <button className="btn-secondary" disabled={busy}
                  onClick={() => { const why = window.prompt('Why is this charge being waived?'); if (why && why.trim().length >= 5) run(() => jobVerticalAPI.charges.waive(c.id, why.trim())); }}>
                  Waive
                </button>
              </div>
            )}
          </div>
        );
      })}

      {!ledgerOnly && <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <select value={pick} onChange={(e) => setPick(e.target.value)} style={{ flex: 1 }}>
          <option value="">Add a fee from the schedule…</option>
          {usable.map((s) => (
            <option key={s.id} value={s.id}>{s.label} — {money(s.amount, s.currency)} ({s.category === 'PLACEMENT' ? 'job-linked' : 'optional'})</option>
          ))}
        </select>
        <button className="btn-primary" disabled={busy || !pick}
          onClick={() => run(async () => { await jobVerticalAPI.charges.create({ job_client: client.id, schedule_item: parseInt(pick, 10) }); setPick(''); })}>
          Add fee
        </button>
      </div>}
      {!ledgerOnly && applicable.length === 0 && <div className="cv-subsection-empty" style={{ marginTop: 4 }}>No fee on the schedule applies to this worker's destination and job type (an admin sets the schedule under Job Operations → Fee schedule).</div>}
    </div>
  );
}
